import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Plus, Pencil, Trash2, X, LogOut, Loader2, Inbox } from "lucide-react";
import Logo from "@/components/Logo";
import { api, formatApiErrorDetail, saveItem, deleteItem, COLLECTIONS, fmtDateShort, resolveImg } from "@/lib/api";
import { PAGES, fieldToText, textToField } from "@/content/pageContent";

const SCHEMAS = {
  projects: {
    label: "Projects",
    titleKey: "title",
    fields: [
      { name: "title", label: "Project Name", type: "text", required: true },
      { name: "category", label: "Category", type: "select", options: ["Community Service", "Club Service", "International Service", "Partners-in-Service", "Entrepreneurship & Professional Development", "Sports", "Public Relations & Marketing", "Publications", "Digital Media", "Zonal Coordinator"] },
      { name: "date", label: "Date", type: "date" },
      { name: "description", label: "Short Description", type: "textarea" },
      { name: "cover_image", label: "Cover Image URL", type: "text" },
      { name: "about", label: "About the Project", type: "textarea" },
      { name: "objective", label: "Objective", type: "textarea" },
      { name: "what_we_did", label: "What We Did", type: "textarea" },
      { name: "participants", label: "Participants", type: "text" },
      { name: "beneficiaries", label: "Beneficiaries", type: "text" },
      { name: "collaborators", label: "Collaborators", type: "text" },
      { name: "impact", label: "Impact", type: "textarea" },
      { name: "related", label: "Related Articles / Publications", type: "text" },
      { name: "gallery", label: "Gallery Image URLs (one per line)", type: "list" },
      { name: "featured", label: "Featured on Home", type: "bool" },
    ],
  },
  blog: {
    label: "Blog Posts",
    titleKey: "title",
    fields: [
      { name: "title", label: "Title", type: "text", required: true },
      { name: "author", label: "Author", type: "text" },
      { name: "category", label: "Category", type: "select", options: ["Member Stories", "Opinion", "Rotaract Stories", "Social Issues", "Experiences", "Leadership", "Campus Life"] },
      { name: "date", label: "Date", type: "date" },
      { name: "excerpt", label: "Short Description", type: "textarea" },
      { name: "content", label: "Full Article (blank line = new paragraph)", type: "textarea", rows: 10 },
      { name: "cover_image", label: "Cover Image URL", type: "text" },
      { name: "featured", label: "Featured", type: "bool" },
    ],
  },
  events: {
    label: "Events & Calendar",
    titleKey: "title",
    fields: [
      { name: "title", label: "Event Name", type: "text", required: true },
      { name: "category", label: "Category", type: "select", options: ["Project", "GBM", "District Event", "Competition", "Fellowship", "Deadline", "Publication", "Special Event"] },
      { name: "date", label: "Date", type: "date", required: true },
      { name: "time", label: "Time", type: "text" },
      { name: "venue", label: "Venue", type: "text" },
      { name: "status", label: "Status", type: "select", options: ["upcoming", "completed"] },
      { name: "description", label: "Description", type: "textarea" },
      { name: "cover_image", label: "Image URL", type: "text" },
    ],
  },
  publications: {
    label: "Publications",
    titleKey: "title",
    fields: [
      { name: "kind", label: "Type", type: "select", options: ["magazine", "newsletter"], required: true },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "period", label: "Period (e.g. Q1 | 2026–27)", type: "text" },
      { name: "month", label: "Month", type: "text" },
      { name: "year", label: "Year", type: "text" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "cover_image", label: "Cover Image URL", type: "text" },
      { name: "link", label: "Reading Link", type: "text" },
    ],
  },
  team: {
    label: "Team",
    titleKey: "name",
    fields: [
      { name: "name", label: "Full Name", type: "text", required: true },
      { name: "position", label: "Position", type: "text", required: true },
      { name: "group", label: "Section", type: "select", options: ["board", "chair"] },
      { name: "department", label: "Department (chairs)", type: "text" },
      { name: "intro", label: "One-line Introduction", type: "text" },
      { name: "photo", label: "Photograph URL", type: "text" },
      { name: "email", label: "Email", type: "text" },
      { name: "order", label: "Display Order", type: "number" },
    ],
  },
  legacy: {
    label: "Legacy (Past Presidents)",
    titleKey: "years",
    fields: [
      { name: "years", label: "Years (e.g. 2017–18)", type: "text", required: true },
      { name: "president", label: "President Name", type: "text", required: true },
      { name: "theme", label: "Annual Theme", type: "text" },
      { name: "note", label: "Note", type: "textarea" },
      { name: "photo", label: "Photograph URL", type: "text" },
    ],
  },
  gallery: {
    label: "Gallery",
    titleKey: "title",
    fields: [
      { name: "title", label: "Title", type: "text", required: true },
      { name: "category", label: "Category", type: "select", options: ["Installation", "Projects", "Fellowships", "GBMs", "District Events", "Team", "Behind the Scenes"] },
      { name: "date", label: "Date", type: "date" },
      { name: "image_url", label: "Image URL", type: "text" },
    ],
  },
  achievements: {
    label: "Achievements",
    titleKey: "title",
    fields: [
      { name: "kind", label: "Type", type: "select", options: ["award", "citation", "recognition", "milestone"], required: true },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "detail", label: "Details", type: "textarea" },
      { name: "date", label: "Date", type: "date" },
    ],
  },
};

const IMG_FIELDS = new Set(["photo", "cover_image", "image_url"]);

function toForm(item, fields) {
  const out = {};
  fields.forEach((f) => {
    const v = item?.[f.name];
    out[f.name] = f.type === "list" ? (Array.isArray(v) ? v.join("\n") : v || "") : v ?? "";
  });
  return out;
}

function fromForm(form, fields) {
  const out = {};
  fields.forEach((f) => {
    const v = form[f.name];
    if (f.type === "list") out[f.name] = v.split("\n").map((s) => s.trim()).filter(Boolean);
    else if (f.type === "bool") out[f.name] = !!v;
    else if (f.type === "number") out[f.name] = v === "" ? undefined : Number(v);
    else out[f.name] = v;
    if (out[f.name] === "" || out[f.name] === undefined) delete out[f.name];
  });
  return out;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/login", { email, password });
      onLogin();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-32">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="card-line w-full max-w-md p-10"
        data-testid="admin-login-form"
      >
        <Logo size={48} />
        <h1 className="mt-8 font-display text-3xl font-semibold text-silver">Admin Portal</h1>
        <p className="mt-2 text-xs text-silver-muted">Sign in to manage the club's content for 2026–27.</p>
        <div className="mt-8 space-y-5">
          <input type="email" required className="input-field" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" />
          <input type="password" required className="input-field" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" />
          <button type="submit" disabled={busy} className="btn-ember w-full justify-center disabled:opacity-60" data-testid="admin-login-btn">
            <span>{busy ? "Signing in…" : "Sign In"}</span>
            {busy && <Loader2 size={14} className="animate-spin" />}
          </button>
        </div>
      </motion.form>
      <Toaster position="bottom-right" theme="dark" />
    </main>
  );
}

function CrudSection({ collectionKey, onSaved }) {
  const schema = SCHEMAS[collectionKey];
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // { id } or "new"
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);

  const handleUpload = async (e, f) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(f.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, [f.name]: data.url }));
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/${collectionKey}`);
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionKey]);

  const openNew = () => {
    setForm(toForm({}, schema.fields));
    setEditing("new");
  };
  const openEdit = (item) => {
    setForm(toForm(item, schema.fields));
    setEditing({ id: item.id });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await saveItem(collectionKey, fromForm(form, schema.fields), editing === "new" ? null : editing.id);
      toast.success("Saved");
      setEditing(null);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item[schema.titleKey]}"? This cannot be undone.`)) return;
    try {
      await deleteItem(collectionKey, item.id);
      toast.success("Deleted");
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`admin-crud-${collectionKey}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-3xl font-semibold text-silver">{schema.label}</h2>
        <button onClick={openNew} className="btn-ember !px-5 !py-2.5" data-testid={`admin-add-${collectionKey}-btn`}>
          <Plus size={14} /> <span>Add New</span>
        </button>
      </div>

      <div className="mt-8 divide-y divide-silver/10 border border-silver/15">
        {(items || []).map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-4 bg-navy/40 px-5 py-4" data-testid={`admin-row-${collectionKey}-${item.id}`}>
            <div className="min-w-0">
              <p className="truncate text-sm text-silver">{item[schema.titleKey] || "(untitled)"}</p>
              <p className="truncate font-mono text-[10px] uppercase tracking-[0.18em] text-silver-muted">
                {item.category || item.kind || item.position || item.group || ""}
                {item.date ? ` · ${fmtDateShort(item.date)}` : item.year ? ` · ${item.year}` : ""}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => openEdit(item)} className="flex h-9 w-9 items-center justify-center border border-silver/20 text-silver-dim transition-colors hover:border-silver/60 hover:text-white" data-testid={`admin-edit-${collectionKey}-${item.id}`}>
                <Pencil size={14} />
              </button>
              <button onClick={() => remove(item)} className="flex h-9 w-9 items-center justify-center border border-silver/20 text-silver-dim transition-colors hover:border-ember/60 hover:text-ember" data-testid={`admin-delete-${collectionKey}-${item.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {items && items.length === 0 && <p className="bg-navy/40 px-5 py-8 text-center text-sm text-silver-muted">Nothing here yet — add the first entry.</p>}
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div className="fixed inset-0 z-[95] flex items-start justify-center overflow-y-auto bg-ink/85 p-4 backdrop-blur-md" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEditing(null)} data-testid="admin-form-modal">
            <motion.form
              onSubmit={save}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
              className="card-line my-10 w-full max-w-2xl p-8"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl text-silver">{editing === "new" ? "Add" : "Edit"} — {schema.label}</h3>
                <button type="button" onClick={() => setEditing(null)} className="text-silver-muted hover:text-white" aria-label="Close form" data-testid="admin-form-close-btn">
                  <X size={20} />
                </button>
              </div>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                {schema.fields.map((f) => (
                  <div key={f.name} className={f.type === "textarea" || f.type === "list" ? "sm:col-span-2" : ""}>
                    <label className="eyebrow mb-2 block">
                      {f.label} {f.required && <span className="text-ember">*</span>}
                    </label>
                    {f.type === "textarea" || f.type === "list" ? (
                      <textarea rows={f.rows || 3} className="input-field resize-y" value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`admin-field-${f.name}`} />
                    ) : f.type === "select" ? (
                      <select className="input-field" value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`admin-field-${f.name}`}>
                        <option value="">— choose —</option>
                        {f.options.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    ) : f.type === "bool" ? (
                      <label className="flex items-center gap-3 text-sm text-silver-dim">
                        <input type="checkbox" checked={!!form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })} className="h-4 w-4 accent-[#E2E8F0]" data-testid={`admin-field-${f.name}`} />
                        Yes
                      </label>
                    ) : (
                      <div className={IMG_FIELDS.has(f.name) ? "flex items-center gap-2" : ""}>
                        <input type={f.type === "number" ? "number" : f.type} className="input-field flex-1" value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`admin-field-${f.name}`} />
                        {IMG_FIELDS.has(f.name) && (
                          <label className="btn-silver shrink-0 cursor-pointer !px-4 !py-2.5" data-testid={`admin-upload-${f.name}`}>
                            <span>{uploading === f.name ? "…" : "Upload"}</span>
                            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleUpload(e, f)} />
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button type="submit" disabled={busy} className="btn-ember mt-8 disabled:opacity-60" data-testid="admin-save-btn">
                <span>{busy ? "Saving…" : "Save"}</span>
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Messages() {
  const [messages, setMessages] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/messages");
      setMessages(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const markRead = async (m) => {
    await api.put(`/admin/messages/${m.id}/read`);
    load();
  };
  const remove = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/messages/${m.id}`);
    load();
  };

  return (
    <div data-testid="admin-messages">
      <h2 className="font-display text-3xl font-semibold text-silver">Inbox</h2>
      <div className="mt-8 space-y-4">
        {(messages || []).map((m) => (
          <div key={m.id} className={`card-line p-6 ${m.read ? "opacity-60" : ""}`} data-testid={`admin-message-${m.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-silver">
                {m.name} <span className="text-silver-muted">· {m.email}</span>
              </p>
              <div className="flex gap-2">
                {!m.read && (
                  <button onClick={() => markRead(m)} className="border border-silver/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-silver-dim hover:text-white" data-testid={`admin-message-read-${m.id}`}>
                    Mark read
                  </button>
                )}
                <button onClick={() => remove(m)} className="border border-silver/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-silver-dim hover:border-ember/60 hover:text-ember" data-testid={`admin-message-delete-${m.id}`}>
                  Delete
                </button>
              </div>
            </div>
            {m.subject && <p className="mt-3 font-display text-xl italic text-silver">{m.subject}</p>}
            <p className="mt-2 text-sm leading-relaxed text-silver-dim">{m.message}</p>
            <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-silver-muted">{fmtDateShort(m.created_at)}</p>
          </div>
        ))}
        {messages && messages.length === 0 && (
          <div className="card-line flex flex-col items-center gap-3 p-12 text-center">
            <Inbox size={22} className="text-silver-muted" />
            <p className="text-sm text-silver-muted">No messages yet — the contact form feeds straight into here.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PageContentEditor() {
  const slugs = Object.keys(PAGES);
  const [slug, setSlug] = useState(slugs[0]);
  const [state, setState] = useState(null); // { slug, values } — only rendered when state.slug matches
  const [busy, setBusy] = useState(false);
  const cfg = PAGES[slug];

  useEffect(() => {
    let live = true;
    const config = PAGES[slug];
    const base = {};
    config.fields.forEach((f) => {
      base[f.key] = f.type === "groups" ? config.defaults[f.key] : fieldToText(f.type, config.defaults[f.key]);
    });
    api
      .get(`/page-content/${slug}`)
      .then(({ data }) => {
        const saved = data?.data || {};
        config.fields.forEach((f) => {
          const v = saved[f.key];
          if (v === undefined || v === null) return;
          if (f.type === "groups") {
            if (Array.isArray(v)) base[f.key] = v;
          } else {
            base[f.key] = fieldToText(f.type, v);
          }
        });
        if (live) setState({ slug, values: base });
      })
      .catch(() => {
        if (live) setState({ slug, values: base });
      });
    return () => {
      live = false;
    };
  }, [slug]);

  // Never render one page's values under another page's fields.
  const values = state && state.slug === slug ? state.values : null;
  const setValues = (updater) =>
    setState((prev) => {
      const next = typeof updater === "function" ? updater(prev.values) : updater;
      return { ...prev, values: next };
    });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const out = {};
      cfg.fields.forEach((f) => {
        out[f.key] = f.type === "groups" ? values[f.key] : textToField(f.type, values[f.key] ?? "");
      });
      await api.put(`/page-content/${slug}`, out);
      toast.success(`${cfg.label} saved — live on the site now`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setGroup = (key, i, patch) =>
    setValues((prev) => ({ ...prev, [key]: prev[key].map((g, gi) => (gi === i ? { ...g, ...patch } : g)) }));

  const [uploading, setUploading] = useState(null);
  const handleUpload = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setValues({ ...values, [key]: data.url });
      toast.success("Uploaded — remember to Save");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="admin-page-content">
      <h2 className="font-display text-3xl font-semibold text-silver">Page Content</h2>
      <p className="mt-2 text-sm text-silver-muted">
        Edit the written copy of any page. Everything you save appears on the website instantly — no code changes
        needed.
      </p>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="page-content-pages">
        {slugs.map((s) => (
          <button
            key={s}
            onClick={() => setSlug(s)}
            className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all ${
              slug === s ? "border-ember bg-ember/10 text-ember" : "border-silver/20 text-silver-muted hover:border-silver/50 hover:text-silver"
            }`}
            data-testid={`page-content-tab-${s}`}
          >
            {PAGES[s].label}
          </button>
        ))}
      </div>

      {!values ? (
        <p className="mt-10 text-sm text-silver-muted">Loading…</p>
      ) : (
        <form onSubmit={save} className="card-line mt-8 space-y-6 p-8" data-testid="page-content-form">
          <p className="eyebrow">{cfg.label}</p>
          {cfg.fields.map((f) => (
            <div key={f.key}>
              <label className="eyebrow mb-2 block">{f.label}</label>
              {f.type === "groups" ? (
                <div className="space-y-4">
                  {values[f.key].map((g, i) => (
                    <div key={i} className="border border-silver/15 p-5" data-testid={`pc-group-${i}`}>
                      <div className="flex items-center justify-between gap-3">
                        <input
                          className="input-field flex-1"
                          placeholder="Group name (e.g. Sponsors)"
                          value={g.title}
                          onChange={(e) => setGroup(f.key, i, { title: e.target.value })}
                          data-testid={`pc-group-${i}-title`}
                        />
                        <button
                          type="button"
                          onClick={() => setValues((prev) => ({ ...prev, [f.key]: prev[f.key].filter((_, gi) => gi !== i) }))}
                          className="flex h-9 w-9 shrink-0 items-center justify-center border border-silver/20 text-silver-dim hover:border-ember/60 hover:text-ember"
                          aria-label="Remove group"
                          data-testid={`pc-group-remove-${i}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <textarea
                        rows={3}
                        className="input-field mt-3 resize-y"
                        placeholder="One name per line — optional logo: Name | image-url"
                        value={(g.items || []).join("\n")}
                        onChange={(e) => setGroup(f.key, i, { items: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                        data-testid={`pc-group-${i}-items`}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setValues((prev) => ({ ...prev, [f.key]: [...prev[f.key], { title: "", items: [] }] }))}
                    className="btn-silver !px-4 !py-2.5"
                    data-testid="pc-group-add"
                  >
                    <Plus size={13} /> <span>Add Group</span>
                  </button>
                </div>
              ) : f.type === "image" ? (
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      className="input-field flex-1"
                      placeholder="Paste an image URL, or upload →"
                      value={values[f.key]}
                      onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                      data-testid={`pc-field-${f.key}`}
                    />
                    <label className="btn-silver shrink-0 cursor-pointer !px-4 !py-2.5" data-testid={`pc-upload-${f.key}`}>
                      <span>{uploading === f.key ? "…" : "Upload"}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e, f.key)} />
                    </label>
                  </div>
                  {values[f.key] ? (
                    <img src={resolveImg(values[f.key])} alt="Preview" className="mt-3 h-28 border border-silver/15 object-cover" data-testid={`pc-preview-${f.key}`} />
                  ) : null}
                </div>
              ) : f.type === "text" ? (
                <input
                  className="input-field"
                  value={values[f.key]}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  data-testid={`pc-field-${f.key}`}
                />
              ) : (
                <textarea
                  rows={f.rows || 4}
                  className="input-field resize-y"
                  value={values[f.key]}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  data-testid={`pc-field-${f.key}`}
                />
              )}
              {f.type === "paragraphs" && <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-silver-muted">Blank line = new paragraph</p>}
              {(f.type === "list" || f.type === "pairs") && <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-silver-muted">One entry per line</p>}
            </div>
          ))}
          <button type="submit" disabled={busy} className="btn-ember disabled:opacity-60" data-testid="pc-save-btn">
            <span>{busy ? "Saving…" : "Save Page Content"}</span>
            {busy && <Loader2 size={14} className="animate-spin" />}
          </button>
        </form>
      )}
    </div>
  );
}


export default function Admin() {
  const [auth, setAuth] = useState("checking"); // checking | out | in
  const [tab, setTab] = useState("projects");

  const check = async () => {
    try {
      await api.get("/auth/me");
      setAuth("in");
    } catch {
      try {
        await api.post("/auth/refresh");
        await api.get("/auth/me");
        setAuth("in");
      } catch {
        setAuth("out");
      }
    }
  };
  useEffect(() => {
    check();
  }, []);

  if (auth === "checking") {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 size={26} className="animate-spin text-silver-muted" />
      </main>
    );
  }
  if (auth === "out") return <Login onLogin={check} />;

  const logout = async () => {
    await api.post("/auth/logout");
    setAuth("out");
  };

  return (
    <main className="min-h-screen px-6 pb-24 pt-28" data-testid="admin-dashboard">
      <Toaster position="bottom-right" theme="dark" />
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="eyebrow">RCSCW · Admin Portal</p>
            <h1 className="mt-3 font-display text-4xl font-semibold metallic">Content Studio</h1>
            <p className="mt-2 text-sm text-silver-muted">Everything you save here appears on the website instantly.</p>
          </div>
          <button onClick={logout} className="btn-silver" data-testid="admin-logout-btn">
            <span>Log Out</span> <LogOut size={13} />
          </button>
        </div>

        <div className="mt-10 flex flex-wrap gap-2 border-b border-silver/10 pb-4" data-testid="admin-tabs">
          {COLLECTIONS.map((c) => (
            <button
              key={c.key}
              onClick={() => setTab(c.key)}
              className={`border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-all ${
                tab === c.key ? "border-ember bg-ember/10 text-ember" : "border-silver/20 text-silver-muted hover:border-silver/50 hover:text-silver"
              }`}
              data-testid={`admin-tab-${c.key}`}
            >
              {c.label}
            </button>
          ))}
          <button
            onClick={() => setTab("messages")}
            className={`border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-all ${
              tab === "messages" ? "border-ember bg-ember/10 text-ember" : "border-silver/20 text-silver-muted hover:border-silver/50 hover:text-silver"
            }`}
            data-testid="admin-tab-messages"
          >
            Inbox
          </button>
          <button
            onClick={() => setTab("pages")}
            className={`border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-all ${
              tab === "pages" ? "border-ember bg-ember/10 text-ember" : "border-silver/20 text-silver-muted hover:border-silver/50 hover:text-silver"
            }`}
            data-testid="admin-tab-pages"
          >
            Page Content
          </button>
        </div>

        <div className="mt-12">
          {tab === "messages" ? <Messages /> : tab === "pages" ? <PageContentEditor /> : <CrudSection key={tab} collectionKey={tab} />}
        </div>
      </div>
    </main>
  );
}
